import { useMemo, type CSSProperties } from 'react';
import { type RestCard } from '@bbkAdminUtils/api-client/rest-experiences-api';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  type BillingAttachedItem,
  type BillingItem,
  type Currency,
  type Frequency,
  type PeriodUnit,
  type Price,
  type PricingTable,
  type Subscription,
} from '@bbkPricingPage/types/pricing-table-types';
import { bbkApiSlice, type BBKRequestContextArgs } from './bbkApi.slice';

// args for loading PC data
export type GetChargebeeItemsArgs = {
  billingSystemIds?: string[];
  itemTypes?: string[];
  appKey: string;
};

export type GetChargebeeItemPriceArgs = {
  billingSystemIds?: string[];
  billingSystemItemIds?: string[];
  priceVariantId?: string;
  appKey: string;
};

export type GetChargebeeAttachedItemsArgs = {
  billingSystemItemIds?: string[];
  appKey: string;
};

// Pricing page args
export type GetPricingPagesArgs = {
  appKey: string;
  active_only?: boolean;
  brand_id?: string;
} & BBKRequestContextArgs;

export type RestPricingPage = {
  id: string;
  name: string;
  application_id: string;
  active: boolean;
  modified_at: string;
  created_at: string;
  updated_at: string;
};

export type GetPricingPageArgs = {
  appKey: string;
  pricingPageId: string;
};

export type GetPricingPageRenderArgs = {
  appKey: string;
  pricingPageId: string;
  publish_state?: 'draft' | 'live';
  priceVariantId?: string;
};

export type CreatePricingPageArgs = {
  appKey: string;
  useV1Api?: boolean;
  body: {
    name: string;
    brand_id?: string;
    table?: {
      cards?: RestCard[];
      pricing_table?: {
        id: string;
        name: string;
        version: string;
        template: string;
        pricing_element: Pick<
          PricingTable,
          | 'grouped_by'
          | 'frequencies'
          | 'currencies'
          | 'items'
          | 'addons'
          | 'customization'
          | 'paywall_config'
        >;
      };
      variant_ids?: string[];
      variants?: Array<{
        variant_id: string;
        items: Array<{ billing_system_id: string; prices: any[] }>;
      }>;
      branding_variant_id?: string;
    };
  };
};

export type UpdatePricingPageArgs = {
  appKey: string;
  pricingPageId: string;
  body: {
    active?: boolean;
    name?: string;
  };
};

export type PublishPricingPageArgs = {
  appKey: string;
  pricingPageId: string;
};

// Pricing page meta types
export type GetPricingPagesMetaArgs = {
  appKey: string;
  brand_id?: string;
  pricing_page_ids?: string[];
};

export type PricingPageMeta = {
  pricing_page_id: string;
  entity_publish_state: 'draft' | 'live' | null;
  is_targeted: boolean;
  play_placements: string[];
};

type RestPricingTable = {
  id: string;
  name: string;
  version: string;
  template: string;
  pricing_element: Pick<
    PricingTable,
    | 'grouped_by'
    | 'frequencies'
    | 'currencies'
    | 'items'
    | 'addons'
    | 'customization'
  >;
};

export type RestPricingPageLayout = {
  cards: RestCard[];
  style_overlay: Partial<
    Partial<EmotionTheme> & {
      font: Partial<{
        fontFamily: CSSProperties['fontFamily'];
        href: string;
        baseFontSize: string;
        baseFontWeight: string;
      }>;
    }
  >;
  pricing_table: RestPricingTable;
  variant_ids?: string[];
  branding_variant_id?: string;
};

export type RestPricingPageJson = {
  experience: {
    version: string;
    layout: RestPricingPageLayout;
  };
  draft_published?: boolean;
  draft_publish_date?: string;
};

export type SaveDraftPricingPageArgs = {
  appKey: string;
  pricingPageId: string;
  useV1Api?: boolean;
  body: {
    cards?: RestCard[];
    pricing_table?: RestPricingTable;
    branding_variant_id?: string;
  };
};

export type GetPreviewSubscriptionsArgs = {
  appKey: string;
};

export type GetBillingSubscriptionsArgs = {
  appKey: string;
  integration: string;
  limit?: number;
  offset?: string;
};

export type BillingSubscriptionsResponse = {
  subscriptions: Subscription[];
  next_offset?: string;
};
export type GetPublicUrlArgs = {
  appKey: string;
  pricingPageId: string;
};

export type GetPublicUrlResponse = {
  url: string;
};

export type ProductFamily = {
  name: string;
  billing_system_id: string;
};

export type GetProductFamiliesArgs = {
  appKey: string;
  itemFamilyIdPrefix?: string;
};

export type GetProductFamilyPlansArgs = {
  appKey: string;
  itemFamilyIds: string[];
  itemIdPrefix?: string;
};

export type PlanMetadata = {
  billing_system_id: string;
  freq: Frequency[];
  curr: Currency[];
  variants: string[];
};

type RawPlanMetadata = {
  billing_system_id: string;
  frequencies: string[];
  currencies: string[];
  variants: string[];
};

export type GetPlanMetadataArgs = {
  appKey: string;
  billingSystemItemIds: string[];
};

export type PriceVariantAttribute = {
  name: string;
  value: string;
};

export type PriceVariant = {
  billing_system_id: string;
  name: string;
  attributes: PriceVariantAttribute[];
};

export type GetPriceVariantsArgs = {
  appKey: string;
  priceVariantIds: string[];
};

export type PricingPageSyncDiffVariant = {
  variant_id: string;
  changes: string[];
};

export type PricingPageSyncDiff = {
  removed_variant_ids: string[];
  modified_variants: PricingPageSyncDiffVariant[];
};

export type PricingPageSyncBillingSystemData = {
  variant_ids: string[];
  items: unknown[];
  attached_addons: unknown[];
  billing_attached_items: unknown[];
  billing_item_prices: unknown[];
};

export type PricingPageSyncResponse = {
  has_differences: boolean;
  diff: PricingPageSyncDiff;
  billing_system_data: PricingPageSyncBillingSystemData;
};

export type PricingPageSyncAcceptResponse = RestPricingPageLayout & {
  variants?: Array<{
    variant_id: string;
    items: Array<{ billing_system_id: string; prices: unknown[] }>;
  }>;
  version?: string;
};

export const pricingPagesSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getChargebeeItems: build.query<BillingItem[], GetChargebeeItemsArgs>({
      query: ({ appKey, billingSystemIds, itemTypes }) => ({
        url: `/api/v1/apps/${appKey}/pricing/items`,
        method: 'GET',
        params: {
          billing_system_ids: billingSystemIds?.join(','),
          item_types: itemTypes?.join(','),
        },
      }),
    }),
    getChargebeeItemPrices: build.query<Price[], GetChargebeeItemPriceArgs>({
      query: ({
        appKey,
        billingSystemIds,
        billingSystemItemIds,
        priceVariantId,
      }) => ({
        url: `/api/v1/apps/${appKey}/pricing/prices`,
        method: 'GET',
        params: {
          billing_system_ids: billingSystemIds?.join(','),
          billing_system_item_ids: billingSystemItemIds?.join(','),
          ...(priceVariantId && { price_variant_id: priceVariantId }),
        },
      }),
    }),
    getChargebeeAttachedItems: build.query<
      BillingAttachedItem[],
      GetChargebeeAttachedItemsArgs
    >({
      query: ({ appKey, billingSystemItemIds }) => ({
        url: `/api/v1/apps/${appKey}/pricing/attached-items`,
        method: 'GET',
        params: {
          billing_system_item_ids: billingSystemItemIds?.join(','),
        },
      }),
    }),
    getZeroDecimalCurrencies: build.query<string[], void>({
      query: () => ({
        url: `/api/v2/zero_decimal_currencies`,
      }),
    }),
    getPricingPagesList: build.query<RestPricingPage[], GetPricingPagesArgs>({
      query: ({ appKey, brand_id, context }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages`,
        params: {
          ...(brand_id && { brand_id }),
          context,
        },
      }),
      providesTags: (result) => [
        { type: 'PricingPage', id: 'LIST' },
        ...(result?.map(({ id }) => ({ type: 'PricingPage' as const, id })) ??
          []),
      ],
    }),
    createPricingPage: build.mutation<RestPricingPage, CreatePricingPageArgs>({
      query: ({ appKey, useV1Api, body }) => ({
        url: useV1Api
          ? `/api/v1/apps/${appKey}/pricing_pages`
          : `/api/v2/apps/${appKey}/pricing-pages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'PricingPage', id: 'LIST' },
        { type: 'PricingPage', id: 'META' },
      ],
    }),
    getPricingPage: build.query<RestPricingPage, GetPricingPageArgs>({
      query: ({ appKey, pricingPageId }) => ({
        url: `/api/v2/apps/${appKey}/pricing-pages/${pricingPageId}`,
      }),
      providesTags: (result, error, { pricingPageId }) => [
        { type: 'PricingPage', id: pricingPageId },
      ],
    }),
    updatePricingPage: build.mutation<RestPricingPage, UpdatePricingPageArgs>({
      query: ({ appKey, pricingPageId, body }) => ({
        url: `/api/v2/apps/${appKey}/pricing-pages/${pricingPageId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { pricingPageId }) => [
        { type: 'PricingPage', id: 'LIST' },
        { type: 'PricingPage', id: pricingPageId },
        { type: 'PricingPage', id: 'META' },
      ],
    }),
    getPricingPageRender: build.query<
      RestPricingPageJson,
      GetPricingPageRenderArgs
    >({
      query: ({ appKey, pricingPageId, publish_state, priceVariantId }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/table`,
        params: {
          ...(publish_state && { publish_state }),
          ...(priceVariantId && { variant_id: priceVariantId }),
        },
      }),
      providesTags: (result, error, args) => [
        { type: 'PricingTable', id: args.pricingPageId },
      ],
    }),
    publishPricingPage: build.mutation<void, PublishPricingPageArgs>({
      query: ({ appKey, pricingPageId }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, args) => [
        { type: 'PricingTable', id: args.pricingPageId },
        { type: 'PricingPage', id: 'META' },
      ],
    }),
    saveDraftPricingPage: build.mutation<void, SaveDraftPricingPageArgs>({
      query: ({ appKey, pricingPageId, body, useV1Api }) => ({
        url: useV1Api
          ? `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/table`
          : `/api/v2/apps/${appKey}/pricing-pages/${pricingPageId}/table`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, args) => [
        { type: 'PricingTable', id: args.pricingPageId },
        { type: 'PricingPage', id: 'META' },
      ],
    }),
    getPreviewSubscriptions: build.query<
      Subscription[],
      GetPreviewSubscriptionsArgs
    >({
      query: ({ appKey }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/preview-subscriptions`,
      }),
    }),
    getBillingSubscriptions: build.query<
      BillingSubscriptionsResponse,
      GetBillingSubscriptionsArgs
    >({
      query: ({ appKey, integration, limit, offset }) => ({
        url: `/api/v1/apps/${appKey}/billing/${integration}/subscriptions`,
        params: {
          ...(limit && { limit }),
          ...(offset && { offset }),
        },
      }),
    }),
    getPublicUrl: build.query<GetPublicUrlResponse, GetPublicUrlArgs>({
      query: ({ appKey, pricingPageId }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/public_url`,
      }),
    }),
    getPricingPagesMeta: build.query<
      PricingPageMeta[],
      GetPricingPagesMetaArgs
    >({
      query: ({ appKey, brand_id, pricing_page_ids }) => ({
        url: `/api/v2/apps/${appKey}/pricing-pages/meta`,
        params: {
          ...(brand_id && { brand_id }),
          ...(pricing_page_ids &&
            pricing_page_ids.length > 0 && {
              pricing_page_ids: pricing_page_ids.join(','),
            }),
        },
      }),
      providesTags: [{ type: 'PricingPage', id: 'META' }],
    }),
    getProductFamilies: build.query<ProductFamily[], GetProductFamiliesArgs>({
      query: ({ appKey, itemFamilyIdPrefix }) => ({
        url: `/api/v1/apps/${appKey}/pricing/item-families`,
        params: {
          ...(itemFamilyIdPrefix && {
            item_family_id_prefix: itemFamilyIdPrefix,
          }),
        },
      }),
    }),
    getProductFamilyPlans: build.query<
      BillingItem[],
      GetProductFamilyPlansArgs
    >({
      query: ({ appKey, itemFamilyIds, itemIdPrefix }) => ({
        url: `/api/v1/apps/${appKey}/pricing/items`,
        params: {
          item_types: 'plan',
          item_family_ids: itemFamilyIds.join(','),
          ...(itemIdPrefix && { item_id_prefix: itemIdPrefix }),
        },
      }),
    }),
    getPlanMetadata: build.query<PlanMetadata[], GetPlanMetadataArgs>({
      query: ({ appKey, billingSystemItemIds }) => ({
        url: `/api/v1/apps/${appKey}/pricing/items/details`,
        params: {
          billing_system_item_ids: billingSystemItemIds.join(','),
        },
      }),
      transformResponse: (response: RawPlanMetadata[]): PlanMetadata[] =>
        response.map((raw) => ({
          billing_system_id: raw.billing_system_id,
          variants: raw.variants ?? ['default'],
          freq: (raw.frequencies ?? []).map((f, i) => {
            const parts = f.split('::');
            const period = parts[0] ?? '1';
            const unit = parts[1] ?? 'month';
            const periodNum = Number(period);
            const periodUnit = unit as PeriodUnit;
            const titleMap: Record<string, string> = {
              '1::day': 'Daily',
              '1::week': 'Weekly',
              '1::month': 'Monthly',
              '1::year': 'Yearly',
            };
            const suffixMap: Record<string, string> = {
              '1::day': 'per day',
              '1::week': 'per week',
              '1::month': 'per month',
              '1::year': 'per year',
            };
            return {
              id: f,
              title:
                titleMap[f] ??
                (periodNum === 1
                  ? unit.charAt(0).toUpperCase() + unit.slice(1) + 'ly'
                  : `Every ${period} ${unit}s`),
              period: periodNum,
              period_unit: periodUnit,
              is_attached: true,
              display_order: i,
              price_suffix:
                suffixMap[f] ??
                (periodNum === 1 ? `per ${unit}` : `per ${period} ${unit}s`),
              recurring_price_label: '',
              show_monthly_price: false,
            };
          }),
          curr: (raw.currencies ?? []).map((code, i) => ({
            id: code,
            currency_code: code,
            is_attached: true,
            display_order: i,
          })),
        })),
    }),
    getPriceVariants: build.query<PriceVariant[], GetPriceVariantsArgs>({
      query: ({ appKey, priceVariantIds }) => ({
        url: `/api/v1/apps/${appKey}/pricing/price-variants`,
        params: {
          price_variant_ids: priceVariantIds.join(','),
        },
      }),
    }),
    getPricingPageSync: build.query<
      PricingPageSyncResponse,
      GetPricingPageArgs
    >({
      // Uses POST (backend caches the diff for ~15 min) but modelled as
      // a query so RTK-Query auto-fires and tag invalidation works normally.
      query: ({ appKey, pricingPageId }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/sync`,
        method: 'POST',
      }),
      providesTags: (result, error, { pricingPageId }) => [
        { type: 'PricingTable', id: `${pricingPageId}-SYNC` },
      ],
    }),
    acceptPricingPageSync: build.mutation<
      PricingPageSyncAcceptResponse,
      GetPricingPageArgs
    >({
      query: ({ appKey, pricingPageId }) => ({
        url: `/api/v1/apps/${appKey}/pricing_pages/${pricingPageId}/sync/accept`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { pricingPageId }) => [
        { type: 'PricingTable', id: pricingPageId },
        { type: 'PricingTable', id: `${pricingPageId}-SYNC` },
        { type: 'PricingPage', id: pricingPageId },
        { type: 'PricingPage', id: 'META' },
      ],
    }),
  }),
});

export const useGetPricingPagesList = (args: GetPricingPagesArgs) => {
  const { data } =
    pricingPagesSlice.endpoints.getPricingPagesList.useQuery(args);
  return useMemo(() => data ?? [], [data]);
};

export const useGetPricingPageRender = (
  args: GetPricingPageRenderArgs,
  options?: { skip?: boolean }
) => {
  const { data, isLoading, error } =
    pricingPagesSlice.endpoints.getPricingPageRender.useQuery(args, {
      skip: options?.skip,
    });
  return useMemo(
    () => ({
      data: data ?? undefined,
      isLoading,
      error,
    }),
    [data, isLoading, error]
  );
};

export const useGetBillingSubscriptions = (
  args: GetBillingSubscriptionsArgs,
  options?: { skip?: boolean }
) => {
  const { data, error, isLoading, isFetching } =
    pricingPagesSlice.endpoints.getBillingSubscriptions.useQuery(args, {
      skip: options?.skip,
    });

  return useMemo(
    () => ({
      subscriptions: data?.subscriptions ?? [],
      nextOffset: data?.next_offset,
      hasMore: !!data?.next_offset,
      isLoading,
      isFetching,
      error,
    }),
    [data, error, isLoading, isFetching]
  );
};
